import React, { useContext, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import '../../presentation/auth/Login.css';
import Icon from '../../../components/icon/Icon';
import 'rodal/lib/rodal.css';
import { Ralert } from '../vhelper/Normarodallalert';
import apiServicewt from '../../../api/apiServicewt';
import Loader from '../vhelper/Loader';
import Popovers from '../../../components/bootstrap/Popovers';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import showNotification from '../../../components/extras/showNotification';
import LANG, { getLangWithKey } from '../../../lang';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '../../../contexts/LanguageSwitchContext';
import { useLoginSettings } from '../../../contexts/loginSettingsContext';
import { userAuth } from '../../../contexts/userContext';
import {
	changeGoogleTranslateLanguage,
	getStoredLanguage,
} from '../../../helpers/languageSwitch';

const LoginHeader = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className='text-center h1 fw-bold mt-5'>Create Account,</div>
				<div className='text-center h4 text-muted mb-5'>Sign up to get started!</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h2 fw-bold mt-5'>Create Account</div>
			<br />
			<br />
			<div>Enter Password below and  create your account</div>
			<br />
		</>
	);
};

const GuestUser = ({ isSignUp }) => {
	const { isLangSwitchEnabled } = useLanguageSwitch();
	const { t } = useTranslation();
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { setToken } = useContext(userAuth);
	const [selectedLanguage, setSelectedLanguage] = useState(getStoredLanguage());
	const [isNewUser] = useState(isSignUp);
	const navigate = useNavigate();
	const lpasswordRef = useRef();
	const cpasswordRef = useRef();
	const [loadalert, setLoadalert] = useState(false);
	const [msgloadalert, setMsgLoadalert] = useState('');
	const [msgtypeloadalert, setMsgtypeLoadalert] = useState('');
	const [isemptypw, setIsemptypw] = useState({ error: false, msg: '*Required' });
	const [isemptycpw, setIsemptycpw] = useState({ error: false, msg: '*Required' });
	const [isCodeValidated, setIsCodeValidated] = useState(false);
	const [isValidationLoading, setIsValidationLoading] = useState(true);
	const [validatedCodeData, setValidatedCodeData] = useState({});
	const [successRedirectPath, setSuccessRedirectPath] = useState('');

	const { loginSettings } = useLoginSettings();
	const loginBgFromSettings = loginSettings?.background_image;
	const defaultLoginBg = `${process.env.PUBLIC_URL}/assets/img/abstract/Main_a.jpg`;
	const loginBg =
		loginBgFromSettings && String(loginBgFromSettings).trim()
			? loginBgFromSettings
			: defaultLoginBg;

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	useEffect(() => {
		document.body.classList.add('auth-page');
		return () => document.body.classList.remove('auth-page');
	}, []);

	useEffect(() => {
		const nextLanguage = getStoredLanguage();
		setSelectedLanguage(nextLanguage);
	}, []);

	const changeLanguage = async (lang) => {
		const nextLanguage = await changeGoogleTranslateLanguage(lang);
		setSelectedLanguage(nextLanguage);

		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(nextLanguage)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(nextLanguage)?.text}`}</span>
			</span>,
		);
	};

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		localStorage.setItem('theme', darkModeStatus ? 'dark' : 'light');
		document.documentElement.setAttribute('theme', darkModeStatus ? 'dark' : 'light');
	}, [darkModeStatus]);

	const closemodel = () => {
		setLoadalert(false);
		if (successRedirectPath) {
			navigate(successRedirectPath, { replace: true });
			return;
		}
		if (!isCodeValidated) {
			navigate('/', { replace: true });
		}
	};
	const startmodel = () => {
		setLoadalert(true);
	};

	const [cshowPassword, setCshowPassword] = useState(false);

	const ctogglePasswordVisibility = () => {
		setCshowPassword(!cshowPassword);
	};

	const [lshowPassword, setLshowPassword] = useState(false);

	const ltogglePasswordVisibility = () => {
		setLshowPassword(!lshowPassword);
	};

	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const setPasswordCode = searchParams.get('set-password-code');

	useEffect(() => {
		if (!setPasswordCode) {
			navigate('/', { replace: true });
		}
	}, [navigate, setPasswordCode]);

	useEffect(() => {
		if (!setPasswordCode) {
			setIsValidationLoading(false);
			return;
		}

		let isMounted = true;

		const validateGuestSetPasswordCode = async () => {
			setIsValidationLoading(true);
			try {
				const item = {
					action: 'check_guest_set_password_code',
					verification_code: setPasswordCode,
				};
				const flechdata = await apiServicewt(item, navigate);
				const responseType = String(flechdata?.type || '').toLowerCase();
				const returnedCode =
					flechdata?.data?.verification_code ||
					flechdata?.verification_code ||
					flechdata?.data?.code ||
					flechdata?.code ||
					flechdata?.data?.set_password_code;
				const isCodeMatch = returnedCode ? returnedCode === setPasswordCode : true;
				const isValidResponse = responseType === 'success' && isCodeMatch;

				if (!isMounted) {
					return;
				}

				if (isValidResponse) {
					setValidatedCodeData(flechdata?.data || {});
					setIsCodeValidated(true);
					setSuccessRedirectPath('');
					setLoadalert(false);
				} else {
					setValidatedCodeData({});
					setIsCodeValidated(false);
					setSuccessRedirectPath('');
					setMsgLoadalert(
						flechdata?.message || t('Invalid or expired set password verification code'),
					);
					setMsgtypeLoadalert('error');
					startmodel();
				}
			} catch (error) {
				if (!isMounted) {
					return;
				}
				setValidatedCodeData({});
				setIsCodeValidated(false);
				setSuccessRedirectPath('');
				setMsgLoadalert(t('Unable to validate the set password verification code'));
				setMsgtypeLoadalert('error');
				startmodel();
			} finally {
				if (isMounted) {
					setIsValidationLoading(false);
				}
			}
		};

		validateGuestSetPasswordCode();

		return () => {
			isMounted = false;
		};
	}, [setPasswordCode, t]);

	const pwchange = async () => {
		let cansendapi = true;
		const minLengthRegex =
			/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[.!@#$%^&*])[A-Za-z\d.!@#$%^&*]{8,}$/;
		if (!lpasswordRef.current.value) {
			setIsemptypw({ error: true, msg: '*Required' });
			cansendapi = false;
		} else if (minLengthRegex.test(`${lpasswordRef.current.value}`.trim())) {
			setIsemptypw({ error: false });
		} else {
			setIsemptypw({
				error: true,
				msg: 'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
			});
			cansendapi = false;
		}
		if (!cpasswordRef.current.value) {
			setIsemptycpw({ error: true, msg: '*Required' });
			cansendapi = false;
		} else if (minLengthRegex.test(`${cpasswordRef.current.value}`.trim())) {
			setIsemptycpw({ error: false });
		} else {
			setIsemptycpw({
				error: true,
				msg: 'Must contain at least 8 characters,including UPPER/lowercase,symbles and numbers',
			});
			cansendapi = false;
		}

		if (cansendapi) {
			const item = {
				action: 'guest_set_password',
				verification_code:
					validatedCodeData?.verification_code ||
					validatedCodeData?.set_password_code ||
					setPasswordCode,
				password: lpasswordRef.current.value,
				confirm_password: cpasswordRef.current.value,
			};
			const flechdata = await apiServicewt(item, navigate);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				if (flechdata?.data?.session_key) {
					setToken(flechdata.data);
					localStorage.setItem('user-auth', JSON.stringify(flechdata.data));
					localStorage.removeItem('guest-user-auth');
					setSuccessRedirectPath('/services');
				} else {
					setSuccessRedirectPath('/login');
				}
				setMsgLoadalert(flechdata.message);
				setMsgtypeLoadalert(flechdata.type);
				startmodel();
			} else {
				setSuccessRedirectPath('');
				startmodel();
				setMsgLoadalert(flechdata.message);
				setMsgtypeLoadalert(flechdata.type);
			}
		}
	};

	if (!setPasswordCode) {
		return null;
	}

	if (isValidationLoading) {
		return <Loader />;
	}

	if (!isCodeValidated) {
		return (
			<Ralert
				msgloadalert={msgloadalert}
				msgtypeloadalert={msgtypeloadalert}
				loadalert={loadalert}
				closemodel={closemodel}
			/>
		);
	}

	return (
		<>
			<Ralert
				msgloadalert={msgloadalert}
				msgtypeloadalert={msgtypeloadalert}
				loadalert={loadalert}
				closemodel={closemodel}
			/>
			<div className='bg-img' style={{ backgroundImage: `url(${loginBg})` }}>
				<PageWrapper title={'Login'}>
					<div
						className='g-3'
						style={{
							position: 'absolute',
							top: 8,
							right: 8,
							display: 'flex',
							justifyContent: 'flex-end',
						}}>
						<div className='col-md-auto'>
							<div className='row g-3'>
								<div className='col-auto'>
									<Popovers trigger='hover' desc='Dark / Light mode'>
										<Button
											{...styledBtn}
											icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
											onClick={() => setDarkModeStatus((prev) => !prev)}
											aria-label='Toggle fullscreen'
											data-tour='dark-mode'
										/>
									</Popovers>
								</div>
								{isLangSwitchEnabled && (
									<div className='col-auto'>
										<Dropdown>
											<DropdownToggle hasIcon={false}>
												<Button
													{...styledBtn}
													icon={getLangWithKey(selectedLanguage)?.icon}
													aria-label='Change language'
													data-tour='lang-selector'
												/>
											</DropdownToggle>
											<DropdownMenu
												isAlignmentEnd
												data-tour='lang-selector-menu'>
												{Object.keys(LANG).map((key) => (
													<DropdownItem key={LANG[key].lng}>
														<Button
															icon={LANG[key].icon}
															onClick={() =>
																changeLanguage(LANG[key].lng)
															}>
															{LANG[key].text}
														</Button>
													</DropdownItem>
												))}
											</DropdownMenu>
										</Dropdown>
									</div>
								)}
							</div>
						</div>
					</div>
					<Page
						className='p-0'
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							minHeight: '100vh',
						}}>
						<Card
							className='shadow-3d-dark'
							data-tour='login-page'
							style={{
								maxWidth: '400px',
								maxHeight: '545px',
							}}>
							<CardBody
								style={{
									justifyContent: 'center',
								}}>
								<div className='text-center my-3'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}>
										<Logo width={200} />
									</Link>
								</div>

								<LoginHeader isNewUser={isNewUser} />

								<form className='row g-4'>
									<div className='col-12'>
										<FormGroup isFloating label='New Password'>
											<Input
												type={lshowPassword ? 'text' : 'password'}
												ref={lpasswordRef}
											/>
											<button
												type='button'
												onClick={ltogglePasswordVisibility}
												style={{
													position: 'absolute',
													right: '10px',
													top: '50%',
													transform: 'translateY(-50%)',
													border: 'none',
													background: 'none',
													cursor: 'pointer',
												}}>
												{lshowPassword ? (
													<Icon
														icon='Visibility'
														size='2x'
														style={{
															color: darkModeStatus
																? '#c9c3c3'
																: 'black',
														}}
													/>
												) : (
													<Icon
														icon='VisibilityOff'
														size='2x'
														style={{
															color: darkModeStatus
																? '#c9c3c3'
																: 'black',
														}}
													/>
												)}{' '}
											</button>
										</FormGroup>
										{isemptypw.error ? (
											<p
												style={{
													color: 'red',
													fontSize: '12px',
												}}>
												{' '}
												{isemptypw.msg ? isemptypw.msg : '*Required'}
											</p>
										) : (
											<br />
										)}
									</div>
									<div className='col-12'>
										<FormGroup isFloating label='Repeat Password'>
											<Input
												type={cshowPassword ? 'text' : 'password'}
												ref={cpasswordRef}
											/>
											<button
												type='button'
												onClick={ctogglePasswordVisibility}
												style={{
													position: 'absolute',
													right: '10px',
													top: '50%',
													transform: 'translateY(-50%)',
													border: 'none',
													background: 'none',
													cursor: 'pointer',
												}}>
												{cshowPassword ? (
													<Icon
														icon='Visibility'
														size='2x'
														style={{
															color: darkModeStatus
																? '#c9c3c3'
																: 'black',
														}}
													/>
												) : (
													<Icon
														icon='VisibilityOff'
														size='2x'
														style={{
															color: darkModeStatus
																? '#c9c3c3'
																: 'black',
														}}
													/>
												)}{' '}
											</button>
										</FormGroup>
										{isemptycpw.error ? (
											<p
												style={{
													color: 'red',
													fontSize: '12px',
												}}>
												{' '}
												{isemptycpw.msg ? isemptycpw.msg : '*Required'}
											</p>
										) : (
											<br />
										)}
									</div>
									<div className='col-12'>
										<Button
											color='warning'
											className='w-100 py-3'
											onClick={() => pwchange()}>
											Submit
										</Button>
									</div>
								</form>
							</CardBody>
						</Card>
					</Page>
				</PageWrapper>
			</div>
		</>
	);
};

LoginHeader.propTypes = {
	isNewUser: PropTypes.bool,
};

LoginHeader.defaultProps = {
	isNewUser: false,
};

GuestUser.propTypes = {
	isSignUp: PropTypes.bool,
};

GuestUser.defaultProps = {
	isSignUp: false,
};

export default GuestUser;
